"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { toast } from "sonner"
import {
  Calendar,
  Users,
  Target,
  TrendingUp,
  Clock,
  AlertCircle,
  BarChart3,
  CalendarDays,
  UserMinus,
  Coffee,
  Zap,
  Trophy,
  Activity,
  Download
} from "lucide-react"

interface TeamMember {
  id: string
  name: string
  availability: number // percentage 0-100
  velocity: number // story points per sprint
}

interface Sprint {
  number: number
  startDate: Date
  endDate: Date
  workingDays: number
  totalCapacity: number
  plannedVelocity: number
}

interface Holiday {
  date: string
  name: string
}

const DEFAULT_HOLIDAYS: Holiday[] = [
  { date: "2024-01-01", name: "New Year's Day" },
  { date: "2024-07-04", name: "Independence Day" },
  { date: "2024-11-28", name: "Thanksgiving" },
  { date: "2024-12-25", name: "Christmas" },
]

export function SprintCalculator() {
  const [sprintLength, setSprintLength] = useState(2) // weeks
  const [startDate, setStartDate] = useState("")
  const [numberOfSprints, setNumberOfSprints] = useState(6)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    { id: "1", name: "Developer 1", availability: 100, velocity: 13 },
    { id: "2", name: "Developer 2", availability: 100, velocity: 8 },
    { id: "3", name: "Developer 3", availability: 80, velocity: 5 },
  ])
  const [includeHolidays, setIncludeHolidays] = useState(true)
  const [holidays, setHolidays] = useState<Holiday[]>(DEFAULT_HOLIDAYS)
  const [sprints, setSprints] = useState<Sprint[]>([])
  const [totalStoryPoints, setTotalStoryPoints] = useState(0)
  const [averageVelocity, setAverageVelocity] = useState(0)
  const [bufferPercentage, setBufferPercentage] = useState(20)
  const [meetingsPercentage, setMeetingsPercentage] = useState(15)

  useEffect(() => {
    let active = true
    queueMicrotask(() => {
      if (!active) return

      const today = new Date()
      const dayOfWeek = today.getDay()
      const daysToMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek
      const nextMonday = new Date(today)
      nextMonday.setDate(today.getDate() + daysToMonday)

      const year = nextMonday.getFullYear()
      const month = String(nextMonday.getMonth() + 1).padStart(2, "0")
      const day = String(nextMonday.getDate()).padStart(2, "0")
      setStartDate(`${year}-${month}-${day}`)
    })

    return () => {
      active = false
    }
  }, [])

  const calculateWorkingDays = useCallback((start: Date, end: Date): number => {
    let workingDays = 0
    const current = new Date(start)

    while (current <= end) {
      const dayOfWeek = current.getDay()
      // Skip weekends
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // Check if it's not a holiday
        if (!includeHolidays || !holidays.some(h => {
          const holidayDate = new Date(h.date)
          return holidayDate.getFullYear() === current.getFullYear() &&
                 holidayDate.getMonth() === current.getMonth() &&
                 holidayDate.getDate() === current.getDate()
        })) {
          workingDays++
        }
      }
      current.setDate(current.getDate() + 1)
    }

    return workingDays
  }, [includeHolidays, holidays])

  const calculateSprints = useCallback(() => {
    if (!startDate) {
      toast.error("Please select a sprint start date")
      return
    }

    const calculatedSprints: Sprint[] = []
    let currentStartDate = new Date(startDate)

    for (let i = 0; i < numberOfSprints; i++) {
      const sprintStartDate = new Date(currentStartDate)
      const sprintEndDate = new Date(currentStartDate)
      sprintEndDate.setDate(sprintEndDate.getDate() + (sprintLength * 7) - 1)

      const workingDays = calculateWorkingDays(sprintStartDate, sprintEndDate)

      // Calculate capacity
      const totalAvailability = teamMembers.reduce((sum, member) => {
        return sum + (member.availability / 100)
      }, 0)

      const effectiveWorkingDays = workingDays * totalAvailability
      const meetingTime = effectiveWorkingDays * (meetingsPercentage / 100)
      const bufferTime = effectiveWorkingDays * (bufferPercentage / 100)
      const totalCapacity = Math.max(0, effectiveWorkingDays - meetingTime - bufferTime)

      // Calculate planned velocity based on team members
      const plannedVelocity = teamMembers.reduce((sum, member) => {
        return sum + (member.velocity * (member.availability / 100))
      }, 0)

      calculatedSprints.push({
        number: i + 1,
        startDate: sprintStartDate,
        endDate: sprintEndDate,
        workingDays,
        totalCapacity: Math.round(totalCapacity * 8), // Convert to hours
        plannedVelocity: Math.round(plannedVelocity)
      })

      // Move to next sprint
      currentStartDate = new Date(sprintEndDate)
      currentStartDate.setDate(currentStartDate.getDate() + 1)

      // Skip weekend if sprint ends on Friday
      if (currentStartDate.getDay() === 6) {
        currentStartDate.setDate(currentStartDate.getDate() + 2)
      }
    }

    setSprints(calculatedSprints)

    // Calculate totals
    const totalPoints = calculatedSprints.reduce((sum, sprint) => sum + sprint.plannedVelocity, 0)
    setTotalStoryPoints(totalPoints)
    setAverageVelocity(totalPoints / numberOfSprints)

    toast.success(`Generated ${numberOfSprints} sprints with ${totalPoints} total story points`)
  }, [startDate, sprintLength, numberOfSprints, teamMembers, calculateWorkingDays, meetingsPercentage, bufferPercentage])

  const addTeamMember = useCallback(() => {
    const newMember: TeamMember = {
      id: Date.now().toString(),
      name: `Developer ${teamMembers.length + 1}`,
      availability: 100,
      velocity: 8
    }
    setTeamMembers([...teamMembers, newMember])
  }, [teamMembers])

  const removeTeamMember = useCallback((id: string) => {
    setTeamMembers(teamMembers.filter(member => member.id !== id))
  }, [teamMembers])

  const updateTeamMember = useCallback((id: string, field: keyof TeamMember, value: string | number) => {
    setTeamMembers(teamMembers.map(member =>
      member.id === id
        ? { ...member, [field]: value }
        : member
    ))
  }, [teamMembers])

  const addHoliday = useCallback(() => {
    const newHoliday: Holiday = {
      date: new Date().toISOString().split('T')[0],
      name: "New Holiday"
    }
    setHolidays([...holidays, newHoliday])
  }, [holidays])

  const removeHoliday = useCallback((index: number) => {
    setHolidays(holidays.filter((_, i) => i !== index))
  }, [holidays])

  const updateHoliday = useCallback((index: number, field: keyof Holiday, value: string) => {
    setHolidays(holidays.map((holiday, i) =>
      i === index
        ? { ...holiday, [field]: value }
        : holiday
    ))
  }, [holidays])

  const exportToCSV = useCallback(() => {
    const headers = ["Sprint", "Start Date", "End Date", "Working Days", "Capacity (hours)", "Planned Velocity"]
    const rows = sprints.map(sprint => [
      sprint.number,
      sprint.startDate.toLocaleDateString(),
      sprint.endDate.toLocaleDateString(),
      sprint.workingDays,
      sprint.totalCapacity,
      sprint.plannedVelocity
    ])

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `sprint-plan-${Date.now()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    toast.success("Sprint plan exported to CSV")
  }, [sprints])

  const getCapacityHealth = () => {
    const totalAvailability = teamMembers.reduce((sum, m) => sum + m.availability, 0) / teamMembers.length
    if (totalAvailability > 90) return "excellent"
    if (totalAvailability > 70) return "good"
    if (totalAvailability > 50) return "fair"
    return "poor"
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Sprint Calculator</CardTitle>
          <CardDescription>
            Calculate sprint dates, capacity, and velocity for agile planning
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="setup" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="setup">
                <Calendar className="h-4 w-4 mr-2" />
                Setup
              </TabsTrigger>
              <TabsTrigger value="team">
                <Users className="h-4 w-4 mr-2" />
                Team
              </TabsTrigger>
              <TabsTrigger value="holidays">
                <Coffee className="h-4 w-4 mr-2" />
                Holidays
              </TabsTrigger>
              <TabsTrigger value="results">
                <BarChart3 className="h-4 w-4 mr-2" />
                Results
              </TabsTrigger>
            </TabsList>

            <TabsContent value="setup" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-date">Sprint Start Date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sprint-length">Sprint Length (weeks)</Label>
                  <Select value={sprintLength.toString()} onValueChange={(v) => setSprintLength(parseInt(v))}>
                    <SelectTrigger id="sprint-length">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 week</SelectItem>
                      <SelectItem value="2">2 weeks</SelectItem>
                      <SelectItem value="3">3 weeks</SelectItem>
                      <SelectItem value="4">4 weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="num-sprints">Number of Sprints: {numberOfSprints}</Label>
                <Slider
                  id="num-sprints"
                  value={[numberOfSprints]}
                  onValueChange={(v) => setNumberOfSprints(v[0])}
                  min={1}
                  max={12}
                  step={1}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="buffer">Buffer Percentage: {bufferPercentage}%</Label>
                <Slider
                  id="buffer"
                  value={[bufferPercentage]}
                  onValueChange={(v) => setBufferPercentage(v[0])}
                  min={0}
                  max={50}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Time reserved for unexpected issues and technical debt
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="meetings">Meetings Percentage: {meetingsPercentage}%</Label>
                <Slider
                  id="meetings"
                  value={[meetingsPercentage]}
                  onValueChange={(v) => setMeetingsPercentage(v[0])}
                  min={0}
                  max={40}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Time allocated for ceremonies, standups, and meetings
                </p>
              </div>

              <Button onClick={calculateSprints} className="w-full">
                <Target className="h-4 w-4 mr-2" />
                Calculate Sprints
              </Button>
            </TabsContent>

            <TabsContent value="team" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Team Members</h3>
                <Button variant="outline" size="sm" onClick={addTeamMember}>
                  <Users className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </div>

              <div className="space-y-4">
                {teamMembers.map((member) => (
                  <Card key={member.id}>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input
                            value={member.name}
                            onChange={(e) => updateTeamMember(member.id, 'name', e.target.value)}
                            placeholder="Team member name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Availability: {member.availability}%</Label>
                          <Slider
                            value={[member.availability]}
                            onValueChange={(v) => updateTeamMember(member.id, 'availability', v[0])}
                            min={0}
                            max={100}
                            step={10}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Velocity (story points)</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              value={member.velocity}
                              onChange={(e) => updateTeamMember(member.id, 'velocity', parseInt(e.target.value) || 0)}
                              min={0}
                              max={50}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeTeamMember(member.id)}
                              disabled={teamMembers.length === 1}
                            >
                              <UserMinus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Team Size</p>
                  <p className="text-2xl font-bold">{teamMembers.length}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Velocity</p>
                  <p className="text-2xl font-bold">
                    {teamMembers.reduce((sum, m) => sum + m.velocity, 0)}
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="holidays" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={includeHolidays}
                    onCheckedChange={setIncludeHolidays}
                  />
                  <Label>Account for holidays</Label>
                </div>
                <Button variant="outline" size="sm" onClick={addHoliday} disabled={!includeHolidays}>
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Add Holiday
                </Button>
              </div>

              {includeHolidays && (
                <div className="space-y-2">
                  {holidays.map((holiday, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="date"
                        value={holiday.date}
                        onChange={(e) => updateHoliday(index, 'date', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        value={holiday.name}
                        onChange={(e) => updateHoliday(index, 'name', e.target.value)}
                        placeholder="Holiday name"
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeHoliday(index)}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Holidays will be excluded from working days calculation. Weekends are always excluded.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="results" className="space-y-4">
              {sprints.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Total Story Points</p>
                            <p className="text-2xl font-bold">{totalStoryPoints}</p>
                          </div>
                          <Trophy className="h-8 w-8 text-yellow-500" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Avg Velocity</p>
                            <p className="text-2xl font-bold">{averageVelocity.toFixed(1)}</p>
                          </div>
                          <TrendingUp className="h-8 w-8 text-green-500" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Capacity Health</p>
                            <Badge variant={getCapacityHealth() === "excellent" ? "default" : getCapacityHealth() === "good" ? "secondary" : "destructive"}>
                              {getCapacityHealth()}
                            </Badge>
                          </div>
                          <Activity className="h-8 w-8 text-blue-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-3">Sprint</th>
                          <th className="text-left p-3">Start Date</th>
                          <th className="text-left p-3">End Date</th>
                          <th className="text-left p-3">Working Days</th>
                          <th className="text-left p-3">Capacity (h)</th>
                          <th className="text-left p-3">Velocity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sprints.map((sprint) => (
                          <tr key={sprint.number} className="border-t">
                            <td className="p-3">
                              <Badge variant="outline">Sprint {sprint.number}</Badge>
                            </td>
                            <td className="p-3">{sprint.startDate.toLocaleDateString()}</td>
                            <td className="p-3">{sprint.endDate.toLocaleDateString()}</td>
                            <td className="p-3">{sprint.workingDays}</td>
                            <td className="p-3">{sprint.totalCapacity}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Zap className="h-4 w-4 text-yellow-500" />
                                {sprint.plannedVelocity}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <Button onClick={exportToCSV} className="w-full">
                    <Download className="h-4 w-4 mr-2" />
                    Export to CSV
                  </Button>
                </>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Results Yet</h3>
                  <p className="text-muted-foreground">
                    Configure your sprint settings and click &ldquo;Calculate Sprints&rdquo; to see results
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Planning Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Velocity Guidelines</h4>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>• New teams: Start with 60-70% of ideal velocity</li>
                <li>• Established teams: Use 3-sprint rolling average</li>
                <li>• Account for learning curve in first sprints</li>
                <li>• Review and adjust after each sprint</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Capacity Planning</h4>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>• Reserve 20-30% buffer for unknowns</li>
                <li>• Account for meetings (15-20% typically)</li>
                <li>• Consider vacation and sick days</li>
                <li>• Plan for sprint ceremonies</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}